import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { Box, Checkbox as CoreCheckbox, Flex, FlexColumn, Text } from 'core/components';

import Model from 'core/model/Model';

import { CancelButton, FormComponent, SubmitButton } from 'core/form';

import InsightsExportOptions from 'app/views/serviceProvider/marketIntel/Insights/InsightsExportOptions';

export const fields = {
  includeGeo: {
    label: 'Include Geo Filter',
    defaultValue: true
  },
  'types._1': {
    label: 'Market Entrance',
    defaultValue: true
  },
  'types._2': {
    label: 'Market Exit',
    defaultValue: true
  },
  'types._3': {
    label: 'Ranking Gain',
    defaultValue: true
  },
  'types._4': {
    label: 'Ranking Loss',
    defaultValue: true
  },
  'types._11': {
    label: 'Start Prefix Origination'
  },
  'types._12': {
    label: 'Stop Prefix Origination'
  },
  'types._13': {
    label: 'Increase Prefix Origination'
  },
  'types._14': {
    label: 'Decrease Prefix Origination'
  },
  'types._101': {
    label: 'Customer Gain',
    defaultValue: true
  },
  'types._102': {
    label: 'Customer Loss',
    defaultValue: true
  },
  'types._103': {
    label: 'Customer Routing Gain'
  },
  'types._104': {
    label: 'Customer Routing Loss'
  },
  'types._201': {
    label: 'Provider Gain'
  },
  'types._202': {
    label: 'Provider Loss'
  },
  'types._203': {
    label: 'Provider Routing Gain'
  },
  'types._204': {
    label: 'Provider Routing Loss'
  },
  magnitude: {
    label: 'Minimum Magnitude',
    defaultValue: [1]
  },
  lookback: {
    label: 'Lookback range',
    rules: 'required',
    defaultValue: 1,
    options: [
      {
        label: 'past 24h',
        value: 1
      },
      {
        label: 'past 7 Days',
        value: 7
      },
      {
        label: 'past 30 Days',
        value: 30
      }
    ]
  },
  ip: {
    label: 'IP Address Family',
    rules: 'required',
    options: [
      {
        label: 'IPv4',
        value: 'v4'
      },
      {
        label: 'IPv6',
        value: 'v6'
      }
    ],
    defaultValue: 'v4'
  },
  market: {
    label: 'Geo Market',
    defaultValue: 'planet$earth',
    transform: {
      in: (value) => {
        if (typeof value === 'string') {
          return value;
        }
        if (value && value.id) {
          return value.id;
        }
        return 'planet$earth';
      }
    }
  },
  widgetTitle: {
    label: 'Title',
    placeholder: 'Add a custom widget title'
  },
  asn: {
    label: 'ASN(s)',
    placeholder: 'Enter comma-separated list of ASNs, e.g. 6123, 3124',
    rules: 'commaSeparatedASNs'
  }
};

const DEFAULT_CONFIG = {
  ip: 'v4',
  lookback: 1,
  includeGeo: true,
  types: {
    _1: true,
    _2: true,
    _3: true,
    _4: true,
    _101: true,
    _102: true,
    _103: false,
    _104: false,
    _201: false,
    _202: false,
    _203: false,
    _204: false
  },
  magnitude: [1],
  market: 'planet$earth',
  title: ''
};

export function getDefaultConfig(props = {}) {
  const { asn } = props;
  const config = DEFAULT_CONFIG;
  if (asn) {
    config.types._201 = true;
    config.types._202 = true;
    config.asn = asn.toString();
  }
  return config;
}

@inject('$auth', '$marketIntel', '$users')
@observer
export default class InsightsConfigForm extends Component {
  state = {
    model: undefined,
    config: undefined
  };

  static getDerivedStateFromProps(props, state) {
    const { $auth, config } = props;

    if (!state.config) {
      const landOnDeck = $auth.getActiveUserProperty('defaultLandingPageType') === 'observationDeck';

      if (config) {
        const configWithDefaults = { ...DEFAULT_CONFIG, ...config };
        return { configWithDefaults, model: new Model(configWithDefaults), landOnDeck, setLandOnDeck: landOnDeck };
      }

      const { marketIntelConfig } = $auth.userSettings;
      if (marketIntelConfig) {
        return {
          config: marketIntelConfig,
          model: new Model(marketIntelConfig),
          landOnDeck,
          setLandOnDeck: landOnDeck
        };
      }
      return { config: DEFAULT_CONFIG, model: new Model(DEFAULT_CONFIG), landOnDeck, setLandOnDeck: landOnDeck };
    }
    return null;
  }

  handleChange = ({ formValues: config }) => {
    const { onChange } = this.props;
    const { model } = this.state;

    if (onChange) {
      model.set(config);
      onChange({ config });
    }
  };

  handleSave = (config) => {
    const { $users, onClose } = this.props;
    const { model, landOnDeck, setLandOnDeck } = this.state;

    model.set(config);
    if (!landOnDeck && setLandOnDeck) {
      $users.updateUserProfile(
        { defaultLandingPageType: 'observationDeck' },
        { toast: false, label: 'Default Landing Page' }
      );
    }
    if (onClose) {
      onClose({ config });
    }
  };

  handleCancel = () => {
    const { onClose } = this.props;
    if (onClose) {
      onClose({});
    }
  };

  handleLandingCheck = (value) => {
    this.setState({ setLandOnDeck: value });
  };

  render() {
    const { $marketIntel, widgetize = false, changeDefault = false, formOnly = false, showTitle = true } = this.props;
    const { model, landOnDeck, setLandOnDeck } = this.state;

    if (!model) {
      return null;
    }

    const { marketName } = $marketIntel.filterObj;

    return (
      <FormComponent
        fields={fields}
        model={model}
        options={{ name: 'Configure Insights' }}
        onChange={this.handleChange}
        handleSave={this.handleSave}
      >
        {({ form }) => (
          <FlexColumn justifyContent="space-between" pb={2}>
            <InsightsExportOptions
              form={form}
              widgetize={widgetize}
              formOnly={formOnly}
              showTitle={showTitle}
              marketName={marketName}
            />
            {changeDefault && (
              <Box py={1} px={2}>
                <Flex alignItems="center">
                  <CoreCheckbox
                    checked={setLandOnDeck}
                    disabled={landOnDeck}
                    onChange={(check) => this.handleLandingCheck(check)}
                    pr={1}
                  />
                  <Text muted={landOnDeck}>Make Observation Deck my default landing page</Text>
                </Flex>
              </Box>
            )}
            {!formOnly && (
              <Flex pt={1} pl={1}>
                <SubmitButton mr={2} />
                <CancelButton onCancel={this.handleCancel} />
              </Flex>
            )}
          </FlexColumn>
        )}
      </FormComponent>
    );
  }
}
